import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { notificationsAPI } from '../services/api';
import './Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create'); // 'create' or 'view'
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [formData, setFormData] = useState({
    userId: 1,
    title: '',
    message: '',
    type: 'info',
    data: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    hasMore: false
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [filters, setFilters] = useState({
    is_read: '',
    type: ''
  });

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchNotifications = async (page = 1) => {
    try {
      setLoading(true);
      const isReadParam = filters.is_read === '' ? null : filters.is_read === 'true';
      const response = await notificationsAPI.getAll(
        1, // userId
        isReadParam,
        filters.type || null,
        page,
        20
      );
      
      if (page === 1) {
        setNotifications(response.data.notifications);
      } else {
        setNotifications(prev => [...prev, ...response.data.notifications]);
      }
      
      setPagination(response.data.pagination);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        userId: formData.userId,
        title: formData.title,
        message: formData.message,
        type: formData.type
      };

      // Parse JSON data if provided
      if (formData.data.trim()) {
        try {
          submitData.data = JSON.parse(formData.data);
        } catch (jsonError) {
          alert('Invalid JSON format in data field');
          return;
        }
      }

      await notificationsAPI.create(submitData);
      alert('Notification created successfully!');
      
      setShowModal(false);
      resetForm();
      fetchNotifications();
    } catch (error) {
      console.error('Failed to create notification:', error);
      alert('Failed to create notification. Please try again.');
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      // Update the notification in the list
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, is_read: true, read_at: new Date().toISOString() }
          : notif
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      alert('Failed to mark notification as read. Please try again.');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (window.confirm('Mark all notifications as read?')) {
      try {
        await notificationsAPI.markAllAsRead();
        setNotifications(prev => prev.map(notif => ({
          ...notif,
          is_read: true,
          read_at: new Date().toISOString()
        })));
        setUnreadCount(0);
        alert('All notifications marked as read!');
      } catch (error) {
        console.error('Failed to mark all notifications as read:', error);
        alert('Failed to mark all notifications as read. Please try again.');
      }
    }
  };

  const handleView = (notification) => {
    setSelectedNotification(notification);
    setModalType('view');
    setShowModal(true);
  };

  const handleCreateNew = () => {
    setModalType('create');
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      userId: 1,
      title: '',
      message: '',
      type: 'info',
      data: ''
    });
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedNotification(null);
    resetForm();
  };

  const loadMoreNotifications = async () => {
    if (pagination.hasMore) {
      fetchNotifications(pagination.page + 1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'info': return 'type-badge-info';
      case 'warning': return 'type-badge-warning';
      case 'success': return 'type-badge-success';
      case 'error': return 'type-badge-error';
      default: return 'type-badge-info';
    }
  };

  if (loading && notifications.length === 0) {
    return (
      <Layout title="Notifications Management">
        <div className="loading">Loading notifications...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Notifications Management">
      <div className="notifications-management">
        <div className="page-header">
          <h2>Notifications Management</h2>
          <div className="header-actions">
            <span className="unread-count">
              {unreadCount} unread of {pagination.total} total
            </span>
            <button 
              className="mark-all-read-btn"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
            >
              Mark All Read
            </button>
            <button 
              className="create-btn"
              onClick={handleCreateNew}
            >
              + Create Notification
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filter-group">
            <label>Filter by Status:</label>
            <select
              name="is_read"
              value={filters.is_read}
              onChange={handleFilterChange}
            >
              <option value="">All Notifications</option>
              <option value="false">Unread Only</option>
              <option value="true">Read Only</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Filter by Type:</label>
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>

        <div className="notifications-table-container">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Type</th>
                <th>Title</th>
                <th>Message</th>
                <th>User ID</th>
                <th>Created</th>
                <th>Read At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notifications.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">No notifications found</td>
                </tr>
              ) : (
                notifications.map((notification) => (
                  <tr key={notification.id} className={!notification.is_read ? 'unread-row' : ''}>
                    <td>
                      <span className={`status-badge ${notification.is_read ? 'read' : 'unread'}`}>
                        {notification.is_read ? '📖' : '📩'}
                      </span>
                    </td>
                    <td>
                      <span className={`type-badge ${getTypeBadgeClass(notification.type)}`}>
                        {getTypeIcon(notification.type)} {notification.type}
                      </span>
                    </td>
                    <td>
                      <div className="notification-title">
                        <span className="title">{notification.title}</span>
                      </div>
                    </td>
                    <td className="message-cell">
                      {notification.message.length > 80 
                        ? `${notification.message.substring(0, 80)}...` 
                        : notification.message
                      }
                    </td>
                    <td>
                      <span className="user-id-badge">
                        {notification.user_id}
                      </span>
                    </td>
                    <td>{formatDate(notification.created_at)}</td>
                    <td>
                      {notification.read_at 
                        ? formatDate(notification.read_at)
                        : '-'
                      }
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="view-btn"
                          onClick={() => handleView(notification)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        {!notification.is_read && (
                          <button 
                            className="mark-read-btn"
                            onClick={() => handleMarkAsRead(notification.id)}
                            title="Mark as Read"
                          >
                            📖
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.hasMore && (
          <div className="load-more-container">
            <button 
              className="load-more-btn"
              onClick={loadMoreNotifications}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More Notifications'}
            </button>
          </div>
        )}

        <div className="pagination-info">
          Showing {notifications.length} of {pagination.total} notifications
          {unreadCount > 0 && (
            <span className="unread-info"> • {unreadCount} unread</span>
          )}
        </div>

        {/* Modal for Create Notification or View Details */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>
                  {modalType === 'create' ? 'Create Notification' : 'Notification Details'}
                </h3>
                <button className="close-btn" onClick={handleCancel}>×</button>
              </div>

              {modalType === 'create' ? (
                <form onSubmit={handleSubmit} className="notifications-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>User ID *</label>
                      <input
                        type="number"
                        name="userId"
                        value={formData.userId}
                        onChange={handleInputChange}
                        placeholder="Enter User ID"
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Type *</label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="info">Info</option>
                        <option value="warning">Warning</option>
                        <option value="success">Success</option>
                        <option value="error">Error</option>
                      </select>
                    </div>

                    <div className="form-group full-width">
                      <label>Title *</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter notification title"
                        maxLength="255"
                        required
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Enter notification message"
                        rows="4"
                        required
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Data (JSON, optional)</label>
                      <textarea
                        name="data"
                        value={formData.data}
                        onChange={handleInputChange}
                        placeholder='{"key": "value", "action": "example"}'
                        rows="3"
                      />
                      <small>Enter valid JSON for custom notification data</small>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="cancel-btn">
                      Cancel
                    </button>
                    <button type="submit" className="submit-btn">
                      Create Notification
                    </button>
                  </div>
                </form>
              ) : (
                <div className="notification-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Status:</label>
                      <span className={`status-badge ${selectedNotification?.is_read ? 'read' : 'unread'}`}>
                        {selectedNotification?.is_read ? '📖 Read' : '📩 Unread'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Type:</label>
                      <span className={`type-badge ${getTypeBadgeClass(selectedNotification?.type)}`}>
                        {getTypeIcon(selectedNotification?.type)} {selectedNotification?.type}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>User ID:</label>
                      <span>{selectedNotification?.user_id}</span>
                    </div>
                    <div className="detail-item">
                      <label>Created:</label>
                      <span>{formatDate(selectedNotification?.created_at)}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Title:</label>
                      <span>{selectedNotification?.title}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Message:</label>
                      <span>{selectedNotification?.message}</span>
                    </div>
                    {selectedNotification?.data && (
                      <div className="detail-item full-width">
                        <label>Data:</label>
                        <pre className="json-data">
                          {JSON.stringify(
                            typeof selectedNotification.data === 'string' 
                              ? JSON.parse(selectedNotification.data)
                              : selectedNotification.data, 
                            null, 
                            2
                          )}
                        </pre>
                      </div>
                    )}
                    {selectedNotification?.read_at && (
                      <div className="detail-item">
                        <label>Read At:</label>
                        <span>{formatDate(selectedNotification.read_at)}</span>
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="cancel-btn">
                      Close
                    </button>
                    {!selectedNotification?.is_read && (
                      <button 
                        type="button" 
                        onClick={() => {
                          handleMarkAsRead(selectedNotification?.id);
                          setSelectedNotification(prev => ({ ...prev, is_read: true }));
                        }}
                        className="mark-read-btn-modal"
                      >
                        Mark as Read
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Notifications;